/**
 * Plattformrollen — Fachprüfer Recht / Fachprüfer FM (tri-server platformRoles.ts).
 *
 * Sie gehören nicht einem Mandanten, sondern der Plattform: vergeben vom Plattform-Root an Nutzer
 * beliebiger Mandanten. Wer eine hat, sieht im Seitenmenü „Regelwerke“ und darf in der
 * Pflichtenprüfung seine Stufe freigeben.
 */

import { atom, useAtom } from "jotai";
import { useEffect } from "react";

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type PlatformRole = "legalReviewer" | "fmReviewer";

export const PLATFORM_ROLES: PlatformRole[] = ["legalReviewer", "fmReviewer"];

export interface PlatformRoleGrant {
  _id: string;
  userId: string;
  email: string;
  customer: string | null;
  customerName?: string | null;
  role: PlatformRole;
  grantedBy: string;
  grantedAt: string;
  note?: string;
  revokedAt?: string | null;
  revokedBy?: string;
  revokeNote?: string;
}

export interface PlatformRoleMailRecipient {
  email: string;
  firstName?: string;
  lastName?: string;
  roles: PlatformRole[];
}

export interface PlatformRoleCandidate {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  customer: string;
  customerName?: string | null;
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // keep the status text
  }
  throw new ApplicationError(message);
};

const request = async (method: string, path: string, body?: any) =>
  processResponse(
    await authenticatedFetch(`${configuration.serverUrl}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  );

export const platformRolesAPI = {
  getMyPlatformRoles: (): Promise<{ roles: PlatformRole[] }> => request("GET", "/me/platform-roles"),
  getGrants: (includeRevoked = false): Promise<{ roles: PlatformRole[]; grants: PlatformRoleGrant[] }> =>
    request("GET", `/administration/platform-roles/grants${includeRevoked ? "?includeRevoked=true" : ""}`),
  searchUsers: (search: string): Promise<PlatformRoleCandidate[]> =>
    request("GET", `/administration/platform-roles/users?search=${encodeURIComponent(search)}`),
  grant: (userId: string, role: PlatformRole, note?: string): Promise<PlatformRoleGrant> =>
    request("POST", "/administration/platform-roles/grants", { userId, role, note }),
  revoke: (grantId: string, note?: string): Promise<PlatformRoleGrant> =>
    request("POST", `/administration/platform-roles/grants/${grantId}/revoke`, { note }),
  mailRecipients: (roles: PlatformRole[]): Promise<PlatformRoleMailRecipient[]> =>
    request("GET", `/administration/platform-roles/mail/recipients?roles=${roles.join(",")}`),
  sendMail: (
    roles: PlatformRole[],
    subject: string,
    body: string,
    test: boolean,
  ): Promise<{ recipients: PlatformRoleMailRecipient[]; sent: number; failed?: string[]; test: boolean }> =>
    request("POST", "/administration/platform-roles/mail", { roles, subject, body, test }),
};

/** The signed-in user's platform roles — undefined until loaded, [] when there are none. */
export const myPlatformRolesAtom = atom<PlatformRole[] | undefined>(undefined);

/**
 * Loads the signed-in user's platform roles once per user (AppShell). Errors count as "none": the
 * entry stays hidden rather than breaking the shell.
 */
export const useLoadMyPlatformRoles = (userKey: string | undefined) => {
  const [, setRoles] = useAtom(myPlatformRolesAtom);
  useEffect(() => {
    if (!userKey) {
      setRoles(undefined);
      return;
    }
    let cancelled = false;
    platformRolesAPI
      .getMyPlatformRoles()
      .then((result) => !cancelled && setRoles(result?.roles || []))
      .catch(() => !cancelled && setRoles([]));
    return () => {
      cancelled = true;
    };
  }, [userKey, setRoles]);
};
